/**
 * Multi-country experience registry.
 * Scalable structure for BR / MX / AR / IN (and beyond) — each country
 * declares its branded experience, transmissions, color palette and
 * locale code. The Brazil hub is the reference implementation.
 *
 * NO hardcoded country logic in components: read `getCountryExperience(code)`.
 */

export interface TransmissionChannel {
  id: string;
  name: string;
  /** Public channel/site URL — VerseSport never redistributes signal. */
  url: string;
  /** Optional embed URL (YouTube live channel only, for now). */
  embedUrl?: string;
  flagEmoji?: string;
  note?: string;
}

export interface CountryExperience {
  /** ISO-3166 alpha-2 country code, uppercased. */
  code: string;
  enabled: boolean;
  locale: string;
  brand: {
    name: string;
    tagline: string;
    flagEmoji: string;
    /** Accent CSS colors used as overlays — never replaces global brand. */
    accents: { primary: string; secondary: string; tertiary: string };
  };
  /** Transmissions VerseSport can accompany (second screen only). */
  transmissions: TransmissionChannel[];
  /** Default selected transmission id. */
  defaultTransmissionId: string;
}

export const COUNTRY_EXPERIENCES: Record<string, CountryExperience> = {
  BR: {
    code: "BR",
    enabled: true,
    locale: "pt-BR",
    brand: {
      name: "🇧🇷 VerseSport Brasil",
      tagline: "A segunda tela oficial da torcida brasileira",
      flagEmoji: "🇧🇷",
      accents: {
        primary: "oklch(0.55 0.18 145)",   // verde
        secondary: "oklch(0.85 0.18 95)",  // amarelo
        tertiary: "oklch(0.45 0.18 255)",  // azul
      },
    },
    transmissions: [
      {
        id: "cazetv",
        name: "CazéTV",
        url: "https://www.youtube.com/@CazeTV",
        embedUrl:
          "https://www.youtube.com/embed/live_stream?channel=UCDk5VVcK_pAJj4xQX2OMpKw&autoplay=0&rel=0&modestbranding=1",
        flagEmoji: "🎙️",
      },
      { id: "globo", name: "TV Globo", url: "https://globoplay.globo.com/agora-na-tv/", flagEmoji: "📺" },
      { id: "sportv", name: "SporTV", url: "https://sportv.globo.com/", flagEmoji: "⚽" },
      { id: "nsports", name: "N Sports", url: "https://nsports.com.br/", flagEmoji: "📡" },
      { id: "globoplay", name: "Globoplay", url: "https://globoplay.globo.com/", flagEmoji: "▶️" },
    ],
    defaultTransmissionId: "cazetv",
  },
  MX: {
    code: "MX",
    enabled: false,
    locale: "es-MX",
    brand: {
      name: "🇲🇽 VerseSport México",
      tagline: "La segunda pantalla de la afición mexicana",
      flagEmoji: "🇲🇽",
      accents: {
        primary: "oklch(0.55 0.18 145)",
        secondary: "oklch(0.65 0.22 25)",
        tertiary: "oklch(0.92 0.02 95)",
      },
    },
    transmissions: [
      { id: "televisa", name: "Televisa", url: "https://www.televisa.com/" },
      { id: "tvazteca", name: "TV Azteca", url: "https://www.tvazteca.com/" },
      { id: "vix", name: "ViX", url: "https://www.vix.com/" },
    ],
    defaultTransmissionId: "televisa",
  },
  AR: {
    code: "AR",
    enabled: false,
    locale: "es-AR",
    brand: {
      name: "🇦🇷 VerseSport Argentina",
      tagline: "La segunda pantalla de la hinchada argentina",
      flagEmoji: "🇦🇷",
      accents: {
        primary: "oklch(0.7 0.12 230)",
        secondary: "oklch(0.95 0.02 95)",
        tertiary: "oklch(0.7 0.12 230)",
      },
    },
    transmissions: [
      { id: "tycsports", name: "TyC Sports", url: "https://www.tycsports.com/" },
      { id: "tvpublica", name: "TV Pública", url: "https://www.tvpublica.com.ar/" },
      { id: "telefe", name: "Telefe", url: "https://mitelefe.com/" },
    ],
    defaultTransmissionId: "tycsports",
  },
  IN: {
    code: "IN",
    enabled: false,
    locale: "en-IN",
    brand: {
      name: "🇮🇳 VerseSport India",
      tagline: "The official second screen for India's fans",
      flagEmoji: "🇮🇳",
      accents: {
        primary: "oklch(0.7 0.15 60)",
        secondary: "oklch(0.96 0.02 95)",
        tertiary: "oklch(0.55 0.18 145)",
      },
    },
    transmissions: [
      { id: "sonyliv", name: "Sony LIV", url: "https://www.sonyliv.com/" },
      { id: "jiocinema", name: "JioCinema", url: "https://www.jiocinema.com/" },
      { id: "starsports", name: "Star Sports", url: "https://www.starsports.com/" },
    ],
    defaultTransmissionId: "sonyliv",
  },
};

export function getCountryExperience(code: string | null | undefined): CountryExperience | null {
  if (!code) return null;
  return COUNTRY_EXPERIENCES[code.toUpperCase()] ?? null;
}

/* ----- transmission persistence (per country, localStorage) ----- */

function storageKey(country: string) {
  return `vs:transmission:${country.toUpperCase()}`;
}

export function getSelectedTransmissionId(country: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(storageKey(country));
  } catch {
    return null;
  }
}

export function setSelectedTransmissionId(country: string, id: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(country), id);
    window.dispatchEvent(new CustomEvent("vs:transmission-changed", { detail: { country, id } }));
  } catch {
    /* ignore */
  }
}

export function resolveSelectedTransmission(
  exp: CountryExperience,
): TransmissionChannel {
  const stored = getSelectedTransmissionId(exp.code);
  if (stored) {
    const found = exp.transmissions.find((t) => t.id === stored);
    if (found) return found;
  }
  return (
    exp.transmissions.find((t) => t.id === exp.defaultTransmissionId) ??
    exp.transmissions[0]
  );
}